import React, { useState } from "react"
import { Loader2 } from "lucide-react"

import { useClientsByUser } from "./hooks/useClientsByUser"
import { useClientUtilization } from "./hooks/useClientUtilization"
import { useClientAuth } from "./hooks/useClientAuth"

import GridCard from "./GridCard"
import CardSection from "./CardSection"

interface Props {
  username: string
  display: { ClinicalExcellence: boolean }
}

const ClinicalExcellenceSection: React.FC<Props> = ({
  username,
  display,
}) => {
  const [selectedClientId, setSelectedClientId] =
    useState<number | null>(null)

  const { data: clients = [], loading: clientsLoading } =
    useClientsByUser(username, display.ClinicalExcellence)

  const { data: utilization, loading: utilLoading } =
    useClientUtilization(selectedClientId)

  const { data: authData, loading: authLoading } =
    useClientAuth(selectedClientId)

  if (!display.ClinicalExcellence) return null

  return (
    <CardSection title="Clinical Excellence">
      {/* LEFT SIDE */}
      <div className="col-span-12 lg:col-span-4">
        {clientsLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading Clients...</span>
          </div>
        ) : (
          <GridCard
            title="Client Metrics"
            headers={[
              "Client",
              "Utilization",
              "Auth Details",
              "Ratio",
            ]}
            data={clients.map((c) => [
              c.initials,
              c.utilization,
              c.authDetails,
              c.ratio,
            ])}
            onRowClick={(index) =>
              setSelectedClientId(clients[index].id)
            }
          />
        )}
      </div>

      {/* RIGHT SIDE */}
      <div className="col-span-12 lg:col-span-8 space-y-4">
        {utilLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading Utilization...</span>
          </div>
        ) : utilization ? (
          <GridCard
            title="Client Utilization"
            headers={[
              "Client ID",
              "Total Requested Hours",
              "Total Hours Used",
              "Total Hours Used Percentage",
              "Unallocated Percentage",
              "Average Requested Hours per Week",
              "Average Hours Used per Week",
              "Total Possible Hours",
            ]}
            data={[Object.values(utilization)]}
            loading={false}
          />
        ) : (
          <div className="text-sm text-muted-foreground">
            Please select a client.
          </div>
        )}

        {authLoading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            <span>Loading Authorization...</span>
          </div>
        ) : authData ? (
          <GridCard
            title="Authorization Details"
            headers={[
              "Insurance Company Name",
              "Authorization Start Date",
              "Authorization End Date",
              "Authorization ID",
              "Billing Code",
              "Total Hours",
              "Total Hours Used to Date",
              "Days Remaining on Auth",
            ]}
            data={authData}
            loading={false}
          />
        ) : null}
      </div>
    </CardSection>
  )
}

export default ClinicalExcellenceSection